/**
 * 设置全局 storage 值，如果无必要，建议使用 model
 */
import { GetUserResponse } from "@/openapi/manage";
import {
  CURRENT_SHOP,
  CURRENT_USER,
  DEFAULT_LOCAL_ENV,
  LOCAL_ENV,
  SUPPLIER,
} from "@/constant";
import type { TypeLocalEnv } from "@/types";
import { Modal } from "antd";
import { PAGES } from "@/constant/pages";
import { StorageKey } from "@/constant/storage";

/**
 * 当前的店铺数据类型
 */
export type ShopData = NonNullable<GetUserResponse["suppliers"]>[0];
/**
 * 店铺不一致页面提示
 */
export const openModalRemindByUnequalShop = () => {
  Modal.warning({
    keyboard: false,
    maskClosable: false,
    title: "店铺切换提示",
    content:
      "检测到您在其他浏览器已切换店铺，为了保证您的数据安全，请关闭页面后重新进入",
    okText: "刷新页面",
    onOk: () => {
      sessionStorage.removeItem(CURRENT_SHOP);
      window.location.replace("/");
    },
  });
};
/**
 * 判断店铺是否一致
 */
export const judgeShopData = () => {
  try {
    const curShop: ShopData = JSON.parse(
      sessionStorage.getItem(CURRENT_SHOP) ?? "{}",
    );
    const globalShop: ShopData = JSON.parse(
      localStorage.getItem(CURRENT_SHOP) ?? "{}",
    );
    const urlParams = new URL(window.location.href);
    const { pathname } = urlParams;
    if (
      curShop.shopId &&
      globalShop.shopId &&
      curShop.shopId !== globalShop.shopId &&
      ![PAGES.CONNECT].includes(pathname as PAGES)
    ) {
      openModalRemindByUnequalShop();
    }
  } catch (e) {}
};
/**
 * 获取当前的店铺数据
 */
export const getCurrentShopData = (): ShopData => {
  try {
    const currentShopStr =
      sessionStorage.getItem(CURRENT_SHOP) ||
      localStorage.getItem(CURRENT_SHOP);
    return JSON.parse(currentShopStr || "{}");
  } catch (e) {
    return {};
  }
};
/**
 * 设置当前店铺数据
 */
export const setCurrentShopData = (shopData: ShopData): void => {
  if (!shopData) {
    sessionStorage.removeItem(CURRENT_SHOP);
  } else {
    try {
      const stringifyShopData = JSON.stringify(shopData);
      sessionStorage.setItem(CURRENT_SHOP, stringifyShopData);
    } catch (e) {
      throw new Error("店铺设置失败");
    }
  }
};
/**
 * 设置全局当前店铺数据
 */
export const setGlobalCurrentShopData = (shopData: ShopData): void => {
  if (!shopData) {
    localStorage.removeItem(CURRENT_SHOP);
  } else {
    try {
      const stringifyShopData = JSON.stringify(shopData);
      localStorage.setItem(CURRENT_SHOP, stringifyShopData);
    } catch (e) {
      throw new Error("店铺设置失败");
    }
  }
};
/**
 * 设置当前用户
 */
export const setCurrentUser = (
  currentUser: GetUserResponse | undefined,
): void => {
  try {
    const stringifyUserData = JSON.stringify(currentUser ?? {});
    localStorage.setItem(CURRENT_USER, stringifyUserData);
  } catch (e) {
    throw new Error("用户设置失败");
  }
};

export const getCurrentUser = (): GetUserResponse => {
  try {
    const stringifyUserData = localStorage.getItem(CURRENT_USER);
    return JSON.parse(stringifyUserData || "{}");
  } catch (e) {
    return {};
  }
};
/**
 * 设置当前店铺
 */
export const setCurrentShop = (res: GetUserResponse | undefined): ShopData => {
  const currentShop = getCurrentShopData();
  const newShop =
    res?.suppliers?.find((item) => item.shopId === currentShop.shopId) ??
    (res?.suppliers?.[0] as ShopData);
  setCurrentShopData(newShop);
  setGlobalCurrentShopData(newShop);
  return newShop;
};
/**
 * 退出登录清理工作
 */
export const logoutClearStorage = (): void => {
  localStorage.removeItem(SUPPLIER);
  localStorage.removeItem(CURRENT_USER);
};
/**
 * 获取token
 */
export const getToken = () => {
  return localStorage.getItem(SUPPLIER);
};
/**
 * 设置token
 */
export const setToken = (token: string) => {
  localStorage.setItem(SUPPLIER, token);
};
/**
 * 获取本地联调环境
 */
export const getLocalEnvStorage = (): TypeLocalEnv => {
  const localEnvData =
    JSON.parse(localStorage.getItem(LOCAL_ENV) || "null") || DEFAULT_LOCAL_ENV;
  return <TypeLocalEnv>localEnvData;
};
/**
 * 设置本地联调环境
 */
export const setLocalEnvStorage = (data: Partial<TypeLocalEnv>): void => {
  const curLocalEnvData = getLocalEnvStorage();
  localStorage.setItem(
    LOCAL_ENV,
    JSON.stringify({ ...curLocalEnvData, ...data }),
  );
};

/**
 * 获取弹窗Key
 */
export const getModalKeysStorage = (): Record<string, number> => {
  const { userId } = getCurrentUser();
  const { shopId } = getCurrentShopData();
  const keys = localStorage.getItem(
    `${StorageKey.MODAL_KEY}_${userId}_${shopId}`,
  );
  try {
    return JSON.parse(keys || "{}");
  } catch (e) {
    localStorage.removeItem(StorageKey.MODAL_KEY);
    return {};
  }
};
/**
 * 设置弹窗Key
 */
export const setModalKeysStorage = (key: string): void => {
  const { userId } = getCurrentUser();
  const { shopId } = getCurrentShopData();
  const keys = getModalKeysStorage();
  localStorage.setItem(
    `${StorageKey.MODAL_KEY}_${userId}_${shopId}`,
    JSON.stringify({
      ...keys,
      [key]: Date.now(),
    }),
  );
};

/**
 * 清除缓存，但保留登录信息
 */
export const clearBrowserCache = () => {
  const keepKeys = [SUPPLIER, CURRENT_SHOP, CURRENT_USER];
  const values: Record<string, string | null> = {};
  keepKeys.forEach((key) => {
    values[key] = localStorage.getItem(key);
  });
  localStorage.clear();
  keepKeys.forEach((key) => {
    if (values[key] !== null) {
      localStorage.setItem(key, values[key]!);
    }
  });
  sessionStorage.clear();
};

/**
 * 通用：按店铺获取设置
 */
const getShopStorage = <T>(storageKey: StorageKey): T | null => {
  const { shopId } = getCurrentShopData();
  if (!shopId) return null;

  try {
    const data = localStorage.getItem(storageKey);
    const settings: Record<string, T> = data ? JSON.parse(data) : {};
    return settings[shopId] ?? null;
  } catch {
    return null;
  }
};

/**
 * 通用：按店铺设置
 */
const setShopStorage = <T>(storageKey: StorageKey, value: T | null): void => {
  const { shopId } = getCurrentShopData();
  if (!shopId) return;

  try {
    const data = localStorage.getItem(storageKey);
    const settings: Record<string, T> = data ? JSON.parse(data) : {};

    if (value === null) {
      delete settings[shopId];
    } else {
      settings[shopId] = value;
    }

    localStorage.setItem(storageKey, JSON.stringify(settings));
  } catch (e) {
    console.error("设置失败", e);
  }
};

/**
 * 获取备货天数设置(按店铺)
 */
export const getStockingDaySettingStorage = (): string | null => {
  return getShopStorage<string>(StorageKey.STOCKING_DAY_SETTING);
};

/**
 * 设置备货天数(按店铺)
 */
export const setStockingDaySettingStorage = (
  stockingDay: string | null,
): void => {
  setShopStorage(StorageKey.STOCKING_DAY_SETTING, stockingDay);
};
